import React from 'react';
import { Field } from 'react-final-form';
import classNames from 'classnames';

import { useIntl } from '../../util/reactIntl';
import { IconClose, ValidationError, HelpText } from '../../components';

import css from './FieldMultiSelect.module.css';

const FieldMultiSelectComponent = props => {
  const {
    rootClassName,
    className,
    id,
    label,
    placeholder,
    helpText,
    options = [],
    required,
    input,
    meta,
  } = props;
  const intl = useIntl();

  if (label && !id) {
    throw new Error('id required when a label is given');
  }

  const { invalid, touched, error } = meta;
  const hasError = touched && invalid && error;

  // Keep the selected values in the same order as the options.
  const selectedKeys = Array.isArray(input.value) ? input.value : [];
  const selectedOptions = options.filter(o => selectedKeys.includes(o.key));
  const availableOptions = options.filter(o => !selectedKeys.includes(o.key));

  const handleSelect = e => {
    const key = e.currentTarget.value;
    if (key) {
      const nextKeys = [...selectedKeys, key];
      input.onChange(options.filter(o => nextKeys.includes(o.key)).map(o => o.key));
    }
  };
  const handleRemove = key => {
    input.onChange(selectedKeys.filter(k => k !== key));
    input.onBlur();
  };

  const classes = classNames(rootClassName || css.root, className);
  return (
    <div className={classes}>
      {label ? (
        <label htmlFor={id}>
          {label}
          {required ? (
            <span className={css.requiredMark} aria-hidden="true">
              {' *'}
            </span>
          ) : null}
        </label>
      ) : null}
      <select
        id={id}
        name={input.name}
        className={classNames({ [css.selectError]: hasError })}
        value=""
        onChange={handleSelect}
        onBlur={() => input.onBlur()}
        onFocus={() => input.onFocus()}
        disabled={availableOptions.length === 0}
        aria-required={required ? 'true' : undefined}
      >
        <option value="" disabled>
          {placeholder || intl.formatMessage({ id: 'FieldMultiSelect.placeholder' })}
        </option>
        {availableOptions.map(o => (
          <option key={o.key} value={o.key}>
            {o.label}
          </option>
        ))}
      </select>
      {selectedOptions.length > 0 ? (
        <ul
          className={css.pills}
          aria-label={intl.formatMessage({ id: 'FieldMultiSelect.selected' }, { label })}
        >
          {selectedOptions.map(o => (
            <li key={o.key} className={css.pill}>
              <span className={css.pillLabel}>{o.label}</span>
              <button
                type="button"
                className={css.removeButton}
                onClick={() => handleRemove(o.key)}
                aria-label={intl.formatMessage(
                  { id: 'FieldMultiSelect.remove' },
                  { option: o.label }
                )}
              >
                <IconClose rootClassName={css.removeIcon} size="small" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <HelpText helpText={helpText} />
      <ValidationError fieldMeta={meta} />
    </div>
  );
};

/**
 * Final Form field for selecting multiple options from a dropdown.
 * Selected options are shown as pills that can be removed.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] add more style rules in addition to components own css.root
 * @param {string} [props.rootClassName] overwrite components own css.root
 * @param {string} props.name name of the field (value is an array of option keys)
 * @param {string} props.id id of the select element
 * @param {ReactNode} [props.label] label of the field
 * @param {string} [props.placeholder] text of the empty option
 * @param {ReactNode} [props.helpText] help text below the field
 * @param {Array<Object>} props.options e.g. [{ key: 's', label: 'Small' }]
 * @param {boolean} [props.required] show the field as mandatory (validate it with props.validate)
 * @param {Function} [props.validate] validator function
 * @returns {JSX.Element} Final Form Field containing a dropdown and pills
 */
const FieldMultiSelect = props => <Field component={FieldMultiSelectComponent} {...props} />;

export default FieldMultiSelect;
